// AutomationRules.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useAutomationRules } from "../../../hooks/useAutomationRules";
import AutomationRuleTable from "../../../components/AutomationRuleTable";
import AddAutomationRuleForm from "../../../components/AddAutomationRuleForm";
import { usePageContext } from "../../../context/PageContext";
import { useTranslation } from "react-i18next";

const AutomationRules = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedRule, setSelectedRule] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
    isFetching,
  } = useAutomationRules(currentPage, pageSize, search);

  useEffect(() => {
    if (error) {
      setErrorMessage(t("automationRulesPage.loadError"));
    } else {
      setErrorMessage(null);
    }
  }, [error, t]);

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setErrorMessage(null);
  };

  const handleDeleteRule = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteRule = () => {
    if (!confirmDelete) return;

    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (error) => {
        setErrorMessage(
          error.response?.data?.message || t("automationRulesPage.deleteOneError")
        );
      },
      onSettled: () => {
        setIsProcessing(false);
      },
    });
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? t("automationRulesPage.bulkDeleteError")
          : t("automationRulesPage.bulkDeletePartial", { failed: failed.length, total: ids.length })
      );
    }
    refetch();
    setIsProcessing(false);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    refetch();
  };

  const handleCloseForm = () => {
    setSelectedRule(null);
    setErrorMessage(null);
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("automationRulesPage.heading")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedRule({})}
        >
          {t("automationRulesPage.addRuleButton")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedRule ? (
        <AddAutomationRuleForm
          rule={selectedRule}
          isFetching={isFetching}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(updatedRule) => {
            setIsProcessing(true);
            updateMutation.mutate(updatedRule, {
              onSuccess: () => {
                refetch();
                handleCloseForm();
              },
              onError: (error) => {
                setErrorMessage(
                  error.response?.data?.message || t("automationRulesPage.saveError")
                );
              },
              onSettled: () => {
                setIsProcessing(false);
              },
            });
          }}
        />
      ) : (
        <AutomationRuleTable
          automationRules={data?.automationRules || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditRule={handleEditRule}
          onDeleteRule={handleDeleteRule}
          onDeleteSelected={handleDeleteSelected}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("automationRulesPage.deleteDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? t("automationRulesPage.processingDelete")
              : t("automationRulesPage.deleteOneConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteRule}>
                {t("shared.delete")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutomationRules;
